import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import { type MouseEvent, type ReactNode, useState } from 'react';
import { MediaThumb } from '../media/shared/MediaThumb';
import { CreativePreview } from './CreativePreview';
import { PREVIEW_PAPER_SX, PREVIEW_POPOVER_SX } from './previewStyles';

interface ThumbnailPreviewProps {
  name: string;
  size: number;
  width?: number | undefined;
  thumbnailUrl: string | null;
  previewUrl: string | null;
  format?: string | null;
  duration?: number | null;
  /** true gives the popup a real player with controls instead of the muted hover loop. */
  controls?: boolean;
}

export function ThumbnailPreview({
  name,
  size,
  width,
  thumbnailUrl,
  previewUrl,
  format = null,
  duration = null,
  controls = false,
}: ThumbnailPreviewProps): ReactNode {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  function handleOpen(event: MouseEvent<HTMLElement>): void {
    if (thumbnailUrl === null && previewUrl === null) return;
    setAnchorEl(event.currentTarget);
  }

  function handleClose(): void {
    setAnchorEl(null);
  }

  return (
    <Box sx={{ display: 'flex' }} onMouseEnter={handleOpen} onMouseLeave={handleClose}>
      <MediaThumb size={size} width={width} src={thumbnailUrl} previewUrl={previewUrl} />
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        disableRestoreFocus
        sx={PREVIEW_POPOVER_SX}
        slotProps={{
          paper: { sx: PREVIEW_PAPER_SX, onMouseEnter: handleOpen, onMouseLeave: handleClose },
        }}
      >
        <CreativePreview
          name={name}
          format={format}
          duration={duration}
          thumbnailUrl={thumbnailUrl ?? ''}
          previewUrl={previewUrl ?? ''}
          controls={controls}
        />
      </Popover>
    </Box>
  );
}
